import { useEffect } from 'react';
import { useConfig } from '../../hooks/useConfig';
/**
 * WeglotWrapper component initializes and configures Weglot translation
 * based on the provided configuration.
 *
 * @param {boolean} enabled - Flag to enable or disable Weglot initialization.
 * @param {string} currentLanguage - The current language code to initialize Weglot with (e.g., 'en', 'es').
 * @param {React.ReactNode} children - The child components to be wrapped.
 *
 * @returns {React.ReactNode} The wrapped child components.
 *
 * @remarks
 * This component uses the `useConfig` hook to fetch configuration data and
 * initializes Weglot with the provided API key when the `enabled` flag
 * is true and the necessary configuration is available.
 */
export const WeglotWrapper = ({ enabled, currentLanguage, children }) => {
    const config = useConfig();
    useEffect(() => {
        if (!enabled || !config?.weglotApiKey) {
            if (enabled && !config?.weglotApiKey) {
                console.warn('Weglot enabled but API key not available in config');
            }
            return;
        }
        if (window?.initializeWeglot) {
            // initialize weglot
            // initializeWeglot should be defined by the parent application typically in weglotScript.cjs
            // The parent application should also have a graphql endpoint named `config` that returns the weglot API key
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            window.initializeWeglot(config.weglotApiKey, currentLanguage || 'en');
        }
        else {
            console.error('initializeWeglot function not found. Make sure the weglotScript.cjs is loaded');
        }
    }, [enabled, config, currentLanguage]);
    return children;
};
